/**
 * Jobs List Page
 *
 * Three concerns:
 *
 * 1. Filter panel UI helpers + customer-autocomplete picker.
 *    Filtering and pagination are handled declaratively by HTMX.
 *
 * 2. Status-change modals (Pause / Cancel) — canonical
 *    ``createDynamicModal`` pattern (see ``form-modals.mdc`` and
 *    ``frontend/src/utils/ui/dynamic-modal.js``). Mirrors what
 *    ``customers.js`` does for the Customer pause / deactivate flows so
 *    the office sees the same affordance everywhere. The modal shells
 *    live in ``templates/office/jobs/_status_change_modal_shells.html``;
 *    GET endpoints render the form body (``_pause_form_body.html`` /
 *    ``_cancel_form_body.html``); POST endpoints return JSON
 *    ``{success: true}`` on success or ``HTTP 422`` + form HTML on
 *    validation errors so the helper paints the response back inline.
 *
 * 3. Resume Job — single-confirmation flow routed through
 *    ``window.showConfirmModal`` (no fields → no need for a dynamic
 *    modal). On confirm we POST straight to ``resume/submit/`` and
 *    soft-reload the page on success. Same ergonomic as the Customer
 *    Activate flow.
 *
 * The legacy right-side ``_job_drawer.html`` has been retired
 * altogether (PO 2026-05-22) : pause / resume / cancel were lifted
 * out of it earlier and the read-only "Job Details" viewer has been
 * folded into the unified ``office:job_detail`` tabbed page. The
 * Eye / 👁 « View » row icon is now a real ``<a href>`` straight to
 * that page (Configuration tab) — no JS dispatch needed.
 */

import { SearchPicker } from '../components/SearchPicker.js';
import { createDynamicModal } from '../utils/ui/dynamic-modal.js';
import { getCsrfToken } from '../utils/csrf.js';
import {
  renderCustomerItem,
  isCustomerDisabled,
  PICKER_MODE_FILTER,
} from '../utils/ui/customer-picker.js';

const state = {
  translations: {},
};

let customerPicker = null;
// PR 2.2 — « Client final » picker singleton, parallel to the customer
// picker above. Cleared on Reset all (see initFilterPanelUI) and
// inspected in tests via window for debug.
let endClientPicker = null;

// Module-level helpers for the two status-change modals. Created lazily
// on first need (after the shells are present in the DOM) and reset on
// every page-level init so a stale reference to a detached modal node
// from a previous boost cycle never sticks around — see
// ``form-modals.mdc`` §9 ("Boosted-nav module caches") for the
// long-form rationale (same trick as ``customers.js``).
let pauseModal = null;
let cancelModal = null;

let afterSettleBound = false;
let actionDelegateBound = false;

export function initJobsList(options = {}) {
  const { hasActiveFilters = false, urls = {} } = options;
  state.translations = options.translations || {};

  // Reset cached helpers so a re-init after an hx-boost swap binds to
  // the fresh modal shells in the new DOM, not to the orphaned ones
  // from the previous page life.
  pauseModal = null;
  cancelModal = null;

  // Globally-exposed entry point. Still kept on window because
  // ``jobs/_drawer_*_form_body.html`` (or any future caller injected
  // via HTMX) can dispatch a status change without re-going through
  // the list delegate.
  window.confirmJobStatusChange = function (jobLabel, jobId, action) {
    confirmStatusChange(jobLabel, jobId, action, state.translations);
  };

  initFilterPanelUI(hasActiveFilters);
  initCustomerPicker(urls);
  // PR 2.2 — gated by the presence of #end-client-search in the DOM
  // (template only renders it when ``subcontracting_enabled`` is True).
  initEndClientPicker(urls);
  bindActionDelegateOnce();
}

// ───────────────────────────────────────────────────────────────────────────
// Delegated handler for the action icons in ``jobs/list.html`` (Phase 13).
// One ``addEventListener`` per page lifetime — re-init from an hx-boost
// re-mount is a no-op because the listener is on ``document.body``.
// ───────────────────────────────────────────────────────────────────────────
//
// Markup contract (replaces the legacy ``onclick="..."``):
//
//   <button data-jobs-action="show-cleaner"
//           data-cleaner-id="{{ emp.id }}">…</button>
//   <button data-jobs-action="status-change"
//           data-job-id="{{ item.job.id }}"
//           data-job-action="pause|resume|cancel"
//           data-customer-name="{{ item.job.customer.name }}">…</button>
//
// Each action routes to the existing ``window.show*Drawer`` /
// ``window.confirmJobStatusChange`` globals — no behaviour change vs.
// the previous ``onclick=`` wiring.
//
// PO 2026-05-22 — the ``show-job`` branch (legacy 👁 « View » icon →
// ``window.showJobDrawer(jobId)``) has been removed alongside the
// unification of Job detail. The row icon is now a real ``<a href>``
// straight to ``office:job_detail`` (see ``templates/office/jobs/
// list.html``), so the browser handles navigation natively.
function bindActionDelegateOnce() {
  if (actionDelegateBound) return;
  actionDelegateBound = true;
  document.body.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-jobs-action]');
    if (!btn) return;
    const action = btn.dataset.jobsAction;
    if (action === 'show-cleaner') {
      const cleanerId = btn.dataset.cleanerId;
      if (cleanerId && typeof window.showCleanerDrawer === 'function') {
        window.showCleanerDrawer(cleanerId);
      }
    } else if (action === 'status-change') {
      const jobId = btn.dataset.jobId;
      const jobAction = btn.dataset.jobAction;
      const customerName = btn.dataset.customerName || '';
      if (jobId && jobAction && typeof window.confirmJobStatusChange === 'function') {
        window.confirmJobStatusChange(customerName, jobId, jobAction);
      }
    }
  });
}

// ───────────────────────────────────────────────────────────────────────────
// Status-change dispatcher
// ───────────────────────────────────────────────────────────────────────────

function confirmStatusChange(jobLabel, jobId, action, translations) {
  // PAUSE → centered modal with date pickers + reason textarea.
  if (action === 'pause') {
    const helper = ensurePauseModalHelper(translations);
    if (!helper) return;
    helper.open(
      `/office/jobs/${jobId}/pause/drawer/`,
      translations.pauseTitle || 'Pause Job',
    );
    return;
  }

  // CANCEL → centered modal with required reason textarea (destructive).
  if (action === 'cancel') {
    const helper = ensureCancelModalHelper(translations);
    if (!helper) return;
    helper.open(
      `/office/jobs/${jobId}/cancel/drawer/`,
      translations.cancelTitle || 'Cancel Job',
    );
    return;
  }

  // RESUME → simple confirm dialog. POSTs straight to resume/submit/
  // because there are no fields to capture.
  if (action === 'resume') {
    confirmResume(jobLabel, jobId, translations);
    return;
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Pause Job modal
// ───────────────────────────────────────────────────────────────────────────
//
// Canonical form-modal pattern (form-modals.mdc):
//   - Static shell: #job-pause-modal (see _status_change_modal_shells.html)
//   - GET endpoint: pause_drawer → _pause_form_body.html
//   - POST endpoint: pause_job_submit → JSON {success: true} or 422 + body
//   - Validation re-render: server returns the form body with bound errors
//     and status=422; the helper paints it back into .modal-content.
//   - Success: helper closes the modal, default onSuccess triggers
//     window._softReload() to refresh the list with the new status.

function ensurePauseModalHelper(translations) {
  if (pauseModal) return pauseModal;
  pauseModal = createDynamicModal({
    modalId: 'job-pause-modal',
    onContentInjected: initFormWidgets,
    errorLoadingMessage: translations.errorLoadingPauseForm || 'Error loading pause form',
  });
  return pauseModal;
}

// ───────────────────────────────────────────────────────────────────────────
// Cancel Job modal
// ───────────────────────────────────────────────────────────────────────────
//
// Same anatomy as the pause modal above. The form body has a single
// required ``cancel_reason`` textarea plus a contextual impact card
// (red, count of future visits about to be cancelled). No widgets
// beyond Flowbite, but we route through the same hook for consistency.

function ensureCancelModalHelper(translations) {
  if (cancelModal) return cancelModal;
  cancelModal = createDynamicModal({
    modalId: 'job-cancel-modal',
    onContentInjected: initFormWidgets,
    errorLoadingMessage: translations.errorLoadingCancelForm || 'Error loading cancel form',
  });
  return cancelModal;
}

function initFormWidgets(content) {
  // Re-attach Flowbite Datepicker to any [datepicker]-decorated input the
  // server just rendered. Each input keeps its own instance so a 422
  // re-render gets a fresh widget rather than the previous one bound to a
  // detached element. Same idempotency guard as customers.js.
  if (typeof window.Datepicker !== 'undefined' && content) {
    content.querySelectorAll('[datepicker]').forEach(function (el) {
      if (!el._datepicker) {
        el._datepicker = new window.Datepicker(el, { format: 'dd/mm/yyyy', autohide: true });
      }
    });
  }
  if (typeof window.reinitFlowbiteWidgets === 'function') {
    window.reinitFlowbiteWidgets(content);
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Resume Job — confirm + direct POST
// ───────────────────────────────────────────────────────────────────────────
//
// No form fields → no dynamic modal needed. Same UX as the Customer
// Activate flow: a single confirmation dialog, then a fire-and-forget
// POST that soft-reloads the list on success. Errors are surfaced via
// showToast (already global) so we keep a tiny code surface here.

function confirmResume(jobLabel, jobId, translations) {
  if (typeof window.showConfirmModal !== 'function') {
    // Fallback for very old pages without showConfirmModal — should never
    // happen in production but keeps the action functional during a
    // partial frontend rollout.
    if (window.confirm(translations.resumeMessage || 'Resume this job?')) {
      postResume(jobId, translations);
    }
    return;
  }

  const titleTpl = translations.resumeTitle || 'Resume {label}?';
  const title = titleTpl.replace('{label}', jobLabel || translations.thisJob || 'this job');

  window.showConfirmModal({
    title: title,
    message: translations.resumeMessage
      || 'This will set the job status to Active and re-generate any future visits cancelled during the pause.',
    confirmText: translations.yesResume || 'Yes, Resume',
    type: 'success',
    onConfirm: function () { postResume(jobId, translations); },
  });
}

function postResume(jobId, translations) {
  fetch(`/office/jobs/${jobId}/resume/submit/`, {
    method: 'POST',
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRFToken': getCsrfToken() || '',
    },
    credentials: 'same-origin',
  })
    .then(function (response) {
      // The endpoint returns JSON for both success and the small set of
      // domain errors (inactive customer, not paused). We tolerate any
      // 4xx with a JSON body too — same as customer activate.
      return response.json().catch(function () { return null; }).then(function (data) {
        return { ok: response.ok, status: response.status, data: data };
      });
    })
    .then(function (result) {
      if (result.ok && result.data && result.data.success) {
        if (typeof window._softReload === 'function') {
          window._softReload();
        } else {
          window.location.reload();
        }
        return;
      }
      const msg = (result.data && result.data.error)
        || translations.errorOccurred
        || 'An error occurred. Please try again.';
      if (typeof window.showToast === 'function') {
        window.showToast(msg, 'error');
      } else {
        window.alert(msg);
      }
    })
    .catch(function (err) {
      console.error('[jobs] resume failed:', err);
      const msg = translations.errorOccurred || 'An error occurred. Please try again.';
      if (typeof window.showToast === 'function') {
        window.showToast(msg, 'error');
      } else {
        window.alert(msg);
      }
    });
}

// ───────────────────────────────────────────────────────────────────────────
// Filter panel + customer picker (unchanged from the previous version)
// ───────────────────────────────────────────────────────────────────────────

function initFilterPanelUI(hasActiveFilters) {
  const panel = document.getElementById('filter-panel');
  const toggleBtn = document.getElementById('toggle-filters-btn');
  const resetBtn = document.getElementById('reset-filters-btn');
  const chevron = document.getElementById('filter-chevron');
  const form = document.getElementById('jobFilterForm');

  if (panel && hasActiveFilters) {
    panel.classList.remove('hidden');
    if (chevron) chevron.classList.add('rotate-180');
  }

  // Per-node idempotency flag as a plain property (not dataset) so it
  // does NOT serialize into HTMX hx-boost history snapshots.
  if (toggleBtn && panel && !toggleBtn._jbFilterBound) {
    toggleBtn._jbFilterBound = true;
    toggleBtn.addEventListener('click', () => {
      const isOpen = panel.classList.toggle('hidden');
      if (chevron) chevron.classList.toggle('rotate-180', !isOpen);
    });
  }

  if (resetBtn && form && !resetBtn._jbResetBound) {
    resetBtn._jbResetBound = true;
    resetBtn.addEventListener('click', () => {
      form.querySelectorAll('select').forEach((sel) => {
        // The status select defaults to "active" (the list's default view),
        // NOT blank / "All" — mirrors the invoice list reset behaviour so
        // resetting returns to the Active view, not every status. Other
        // selects clear to their empty option.
        sel.value = sel.name === 'status' ? 'active' : '';
        const wrapper = sel.closest('.custom-select-wrapper');
        const textEl = wrapper?.querySelector('.custom-select-text');
        const opt = sel.selectedOptions[0] || sel.options[0];
        if (textEl && opt) textEl.textContent = opt.text;
      });

      form.querySelectorAll('input[type="text"]').forEach((inp) => {
        inp.value = '';
      });

      form.querySelectorAll('input[type="hidden"]').forEach((inp) => {
        inp.value = '';
      });

      if (customerPicker) customerPicker.clear();
      // PR 2.2 — clear the EndClient picker too. The select-clear loop
      // above already wipes the hidden input, but the visible text
      // input is owned by SearchPicker and needs its own ``clear()``
      // to also dismiss any open dropdown / reset the search state.
      if (endClientPicker) endClientPicker.clear();

      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    });
  }
}

function initCustomerPicker(urls) {
  const customerInput = document.getElementById('customer-search');
  if (!customerInput) return;

  customerPicker = new SearchPicker({
    inputId: 'customer-search',
    hiddenInputId: 'customer-filter',
    dropdownId: 'customer-search-results',
    // Filter mode → ON_HOLD + INACTIVE included so the office can list
    // historical jobs of paused / ended customers (still in the table).
    endpoint: (urls.customerSearch || '/office/api/customers/search/') + '?include=all',
    maxResults: 20,
    minChars: 0,
    renderItem: (item) => renderCustomerItem(item, { mode: PICKER_MODE_FILTER }),
    isItemDisabled: (item) => isCustomerDisabled(item, PICKER_MODE_FILTER),
    onSelect: () => {
      const form = document.getElementById('jobFilterForm');
      if (form) form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    },
    onClear: () => {
      const form = document.getElementById('jobFilterForm');
      if (form) form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    },
  });
}

/**
 * PR 2.2 — « Client final » (EndClient) picker. Mirrors the customer
 * picker contract above. Early-returns when the DOM input isn't
 * present, which is the case for opted-out orgs (the template gates
 * the markup on ``subcontracting_enabled``).
 *
 * ``?include=all`` surfaces archived EndClients because the office
 * may legitimately want to filter on a deactivated donneur d'ordre's
 * historical jobs (same UX rationale as the customer picker).
 */
function initEndClientPicker(urls) {
  const endClientInput = document.getElementById('end-client-search');
  if (!endClientInput) return;

  endClientPicker = new SearchPicker({
    inputId: 'end-client-search',
    hiddenInputId: 'end-client-filter',
    dropdownId: 'end-client-search-results',
    endpoint: (urls.endClientSearch || '/office/api/end_clients/search/') + '?include=all',
    maxResults: 20,
    minChars: 0,
    renderItem: renderEndClientItem,
    onSelect: () => {
      const form = document.getElementById('jobFilterForm');
      if (form) form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    },
    onClear: () => {
      const form = document.getElementById('jobFilterForm');
      if (form) form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    },
  });
}

/**
 * Renderer for a single EndClient row in the filter-bar autocomplete
 * dropdown (NOT the customer-form picker — that one was retired in
 * PR 2.1f-c). Kept inline because it's used by exactly two pages
 * (visits + jobs) and the markup is trivial; same rationale as the
 * visits.js mirror. Extract to a shared helper on the 3rd consumer.
 */
function renderEndClientItem(item) {
  const name = item.name || '';
  const dim = item.is_active === false ? 'opacity-60' : '';
  const badge = item.is_active === false
    ? ` <span class="ml-2 text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Archived</span>`
    : '';
  const safeName = escapeHtmlJobs(name);
  // No leading emoji / icon — matches the customer dropdown renderer
  // which is text-only. The picker's input icon (office tower) carries
  // the visual context.
  return `
    <div class="flex items-center px-3 py-2 ${dim}">
      <span class="text-sm font-medium text-gray-900 dark:text-white">${safeName}</span>${badge}
    </div>
  `;
}

function escapeHtmlJobs(text) {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function bindAfterSettleOnce() {
  if (afterSettleBound) return;
  afterSettleBound = true;
  document.addEventListener('htmx:afterSettle', (e) => {
    const target = e.detail && e.detail.target;
    if (!target || target.id !== 'list-results') return;
    if (typeof window.reinitFlowbiteWidgets === 'function') {
      window.reinitFlowbiteWidgets(target);
    }
  });
}

// ───────────────────────────────────────────────────────────────────────────
// Job detail page (``office:job_detail``) — page-actions wiring
// ───────────────────────────────────────────────────────────────────────────
//
// PO 2026-05-22 — mirrors what ``customer-detail.js::initCustomerDetail``
// does for the Customer detail page : wires the status-change icons
// (Pause / Resume / Cancel) in the page header so they behave
// identically to those on the list page.
//
// We do NOT call ``initJobsList()`` from here because that function
// also calls ``initFilterPanelUI`` which queries ``#filter-panel`` /
// ``#toggle-filters-btn`` ids — ids that ALSO exist on the Visites
// tab (it embeds the standalone visits-list partial that owns those
// ids natively). Re-binding the visits-tab filter toggle through the
// jobs-list filter-panel init would double-bind it on every boosted
// re-init (see ``.cursor/rules/hx-boost-init.mdc`` §2 for the
// canonical bug class).
//
// Surface needed by the detail page:
//   * ``window.confirmJobStatusChange`` global (used by the
//     ``data-jobs-action="status-change"`` icons in detail.html ;
//     same contract as on jobs/list.html).
//   * ``bindActionDelegateOnce`` — the document.body click delegate
//     that routes those icons to the global. Module-level flag is
//     boost-safe (rule 1).
//
// ``initJobDetail`` is intentionally minimal so the Job detail page
// can be re-rendered through hx-boost without re-binding shared
// filter-panel listeners. The pause / cancel modal shells must be
// present in the DOM — the detail.html template includes
// ``_status_change_modal_shells.html`` for that.

export function initJobDetail(options = {}) {
  state.translations = options.translations || {};

  // Reset cached modal helpers so a re-init after an hx-boost swap
  // binds to the fresh modal shells in the new DOM, not to the
  // orphaned ones from the previous page life (same trick as
  // ``initJobsList``).
  pauseModal = null;
  cancelModal = null;

  window.confirmJobStatusChange = function (jobLabel, jobId, action) {
    confirmStatusChange(jobLabel, jobId, action, state.translations);
  };

  bindActionDelegateOnce();
}

export function autoInitJobDetail() {
  const root = document.querySelector('[data-auto-init="jobDetail"]');
  if (!root) return;
  const ds = root.dataset;
  initJobDetail({
    translations: {
      pauseTitle: ds.trPauseTitle || 'Pause Job',
      cancelTitle: ds.trCancelTitle || 'Cancel Job',
      resumeTitle: ds.trResumeTitle || 'Resume {label}?',
      thisJob: ds.trThisJob || 'this job',
      resumeMessage: ds.trResumeMessage
        || 'This will set the job status to Active and re-generate any future visits cancelled during the pause.',
      yesResume: ds.trYesResume || 'Yes, Resume',
      errorLoadingPauseForm: ds.trErrorPause || 'Error loading pause form',
      errorLoadingCancelForm: ds.trErrorCancel || 'Error loading cancel form',
      errorOccurred: ds.trErrorOccurred || 'An error occurred. Please try again.',
    },
  });
}


/**
 * Auto-init entry point (wired via ``data-auto-init="jobsList"``).
 * Called by _runAutoInit in app.js on hx-boost afterSwap and history
 * restore — no inline <script> in the template (CSP).
 */
export function autoInitJobsList() {
  const root = document.querySelector('[data-auto-init="jobsList"]');
  if (!root) return;
  const ds = root.dataset;
  initJobsList({
    hasActiveFilters: ds.hasActiveFilters === '1',
    urls: {
      customerSearch: ds.customerSearchUrl || '/office/api/customers/search/',
      // PR 2.2 — fall back to the canonical URL when the data-attribute
      // is absent (opted-out org templates don't emit it). The picker
      // is also gated by DOM presence in initEndClientPicker(), so the
      // URL is only ever used when the corresponding input exists.
      endClientSearch: ds.endClientSearchUrl || '/office/api/end_clients/search/',
    },
    translations: {
      pauseTitle: ds.trPauseTitle || 'Pause Job',
      cancelTitle: ds.trCancelTitle || 'Cancel Job',
      resumeTitle: ds.trResumeTitle || 'Resume {label}?',
      thisJob: ds.trThisJob || 'this job',
      resumeMessage: ds.trResumeMessage
        || 'This will set the job status to Active and re-generate any future visits cancelled during the pause.',
      yesResume: ds.trYesResume || 'Yes, Resume',
      errorLoadingPauseForm: ds.trErrorPause || 'Error loading pause form',
      errorLoadingCancelForm: ds.trErrorCancel || 'Error loading cancel form',
      errorOccurred: ds.trErrorOccurred || 'An error occurred. Please try again.',
    },
  });
  bindAfterSettleOnce();
}

window.initJobsList = initJobsList;
